/**
 * Camera arithmetic on the map engine's world (MapLibre plan D9).
 *
 * One pure module, so the opening frame, the capped fits and the test fake's
 * camera all compute the same numbers. MapLibre's world is 512 px wide at
 * zoom 0 (Leaflet's was 256), so an engine zoom is one less than the server's
 * (`viewport.ts` converts, in one place).
 *
 * Coordinates are `[lat, lng]` pairs, the order the rest of this app uses;
 * the engine's own `[lng, lat]` order appears only at its call sites.
 */

/** Pixels across the whole world at engine zoom 0. */
export const WORLD_PX_AT_ZOOM_0 = 512;

/** The Web Mercator limit (Leaflet's `SphericalMercator.MAX_LATITUDE`):
 *  beyond it the projection runs to infinity. */
export const MAX_LATITUDE = 85.0511287798;

/** The cap when a fit names none: a single point has no extent, so without a
 *  cap it would ask for an infinite zoom. 22 is MapLibre's own `maxZoom`
 *  default — a fit must not ask for a zoom the engine refuses — and the
 *  mobile port's value (plan D9). */
const DEFAULT_MAX_ZOOM = 22;

export type LatLng = [number, number];

export interface Box {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface FitOptions {
  /** Pixels kept clear on every side of the box. */
  padding?: number;
  /** A FIT cap: the fit never zooms closer than this. Not an interaction limit. */
  maxZoom?: number;
  /** The platform's interaction floor (`ENGINE_MIN_ZOOM`). */
  minZoom: number;
}

export interface Camera {
  centre: LatLng;
  zoom: number;
  /** True when a floor (the platform's or the view's height) raised the zoom,
   *  i.e. the box does not fit and the camera shows what the engine allows. */
  floored: boolean;
}

const clampLat = (lat: number): number => Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat));

const worldPx = (zoom: number): number => WORLD_PX_AT_ZOOM_0 * Math.pow(2, zoom);

/** Projected x, y in pixels on a world `world` pixels wide. y grows southward. */
const project = (lat: number, lng: number, world: number): [number, number] => {
  const phi = (clampLat(lat) * Math.PI) / 180;
  const x = ((lng + 180) / 360) * world;
  const y = (0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI)) * world;
  return [x, y];
};

const unproject = (x: number, y: number, world: number): LatLng => {
  const lng = (x / world) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / world;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return [lat, lng];
};

/**
 * The box `km` either side of `centre`: full width and height are 2 × km, the
 * arithmetic of Leaflet's `LatLng.toBounds(km * 2000)`, which both clients'
 * opening frames used.
 */
export const radiusBounds = (centre: LatLng, km: number): Box => {
  const [lat, lng] = centre;
  const latAccuracy = (180 * km * 2000) / 40075017;
  const lngAccuracy = latAccuracy / Math.cos((Math.PI / 180) * lat);
  return {
    west: lng - lngAccuracy,
    south: lat - latAccuracy,
    east: lng + lngAccuracy,
    north: lat + latAccuracy,
  };
};

/**
 * The camera that fits `box` into a view of `size`, as the engine will draw it.
 *
 * In order: latitudes clamp to ±MAX_LATITUDE; the tighter axis sets the zoom
 * over the padded size; `maxZoom` caps it; then the floor is
 * `max(minZoom, log2(height / 512))` — the engine never lets the world be
 * shorter than the view (MapLibre's latitude constraint), taken over the FULL
 * height, never the padded one; finally the centre's Mercator y is clamped to
 * `[height / 2, worldPx − height / 2]` so the view's top and bottom stay inside
 * the world. x is never clamped: the engine leaves longitude free.
 */
export const cameraForBounds = (box: Box, size: Size, options: FitOptions): Camera => {
  const padding = options.padding ?? 0;
  const maxZoom = options.maxZoom ?? DEFAULT_MAX_ZOOM;
  const [xWest, yNorth] = project(box.north, box.west, WORLD_PX_AT_ZOOM_0);
  const [xEast, ySouth] = project(box.south, box.east, WORLD_PX_AT_ZOOM_0);
  const spanX = Math.abs(xEast - xWest);
  const spanY = Math.abs(ySouth - yNorth);
  // A container narrower than its padding has no room; one pixel keeps the
  // logarithm finite instead of NaN.
  const roomX = Math.max(1, size.width - 2 * padding);
  const roomY = Math.max(1, size.height - 2 * padding);
  const asked = Math.min(Math.log2(roomX / spanX), Math.log2(roomY / spanY));
  const capped = Math.min(asked, maxZoom);
  const floor = Math.max(options.minZoom, Math.log2(size.height / WORLD_PX_AT_ZOOM_0));
  const zoom = Math.max(capped, floor);
  const floored = capped < floor;

  const world = worldPx(zoom);
  const scale = world / WORLD_PX_AT_ZOOM_0;
  const cx = ((xWest + xEast) / 2) * scale;
  const rawY = ((yNorth + ySouth) / 2) * scale;
  const half = size.height / 2;
  // When the world is exactly as tall as the view the two bounds meet; min
  // before max keeps the result inside even if float noise crosses them.
  const cy = Math.max(half, Math.min(world - half, rawY));
  return { centre: unproject(cx, cy, world), zoom, floored };
};

/** What a camera shows: the box a view of `size` covers at `centre`/`zoom`.
 *  Longitudes are unbounded, as the engine reports them. */
export const boundsForCamera = (centre: LatLng, zoom: number, size: Size): Box => {
  const world = worldPx(zoom);
  const [cx, cy] = project(centre[0], centre[1], world);
  const [north, west] = unproject(cx - size.width / 2, cy - size.height / 2, world);
  const [south, east] = unproject(cx + size.width / 2, cy + size.height / 2, world);
  return { west, south, east, north };
};
